import React, { useEffect, useState, useContext, useCallback } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
    ModalFooter, ModalCloseButton, Button, VStack, HStack, Text,
    Input, FormControl, FormLabel, RadioGroup, Radio, Box, Icon,
    Badge, Divider, useToast, Heading
} from '@chakra-ui/react';
import { MdCalendarMonth, MdCheckCircle, MdSend } from 'react-icons/md';
import AuthContext from '../services/AuthContext';
import axiosInstance from '../services/axiosInstance';

const PRIMARY = '#F2C919';
const DARK = '#2C3E50';

export default function ScheduleFormPopup() {
    const { user } = useContext(AuthContext);
    const toast = useToast();

    const [pendingForm, setPendingForm] = useState(null);
    const [isOpen, setIsOpen] = useState(false);

    const [fullName, setFullName] = useState('');
    const [whatsappNumber, setWhatsappNumber] = useState('');
    const [session1Choice, setSession1Choice] = useState('');
    const [session2Choice, setSession2Choice] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const token = localStorage.getItem('userToken');
    const headers = { Authorization: `Bearer ${token}` };

    const fetchPending = useCallback(async () => {
        if (!user || user.role !== 'student') return;
        try {
            const { data } = await axiosInstance.get('/schedule-forms/pending/me', { headers });
            const forms = data.data || [];
            // Filter out ones already dismissed in this session
            const nextPending = forms.find(f => !localStorage.getItem(`scheduleDone_${f._id}`));
            if (nextPending) {
                setPendingForm(nextPending);
                setIsOpen(true);
            }
        } catch (e) {
            // Silently fail — popup is non-critical
        }
    }, [user]);

    useEffect(() => {
        // Only check after a short delay to let the page load
        const timer = setTimeout(fetchPending, 2000);
        return () => clearTimeout(timer);
    }, [fetchPending]);

    const handleSubmit = async () => {
        if (!fullName.trim() || !whatsappNumber.trim() || !session1Choice || !session2Choice) {
            return toast({ title: 'يرجى ملء جميع الحقول', status: 'warning', duration: 3000, isClosable: true });
        }
        setSubmitting(true);
        try {
            await axiosInstance.post(`/schedule-forms/${pendingForm._id}/submit`, {
                fullName: fullName.trim(),
                whatsappNumber: whatsappNumber.trim(),
                session1Choice,
                session2Choice,
                studentId: user._id,
            }, { headers });

            // Mark as done in localStorage so it never shows again
            localStorage.setItem(`scheduleDone_${pendingForm._id}`, '1');
            setSubmitted(true);
        } catch (e) {
            toast({
                title: 'خطأ',
                description: e.response?.data?.error || 'حدث خطأ',
                status: 'error', duration: 4000, isClosable: true
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        // Allow closing (they'll see it again next login unless submitted)
        setIsOpen(false);
    };

    const handleDone = () => {
        setIsOpen(false);
        setPendingForm(null);
        setSubmitted(false);
        setFullName('');
        setWhatsappNumber('');
        setSession1Choice('');
        setSession2Choice('');
    };

    if (!pendingForm) return null;

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="lg" closeOnOverlayClick={false} isCentered>
            <ModalOverlay backdropFilter="blur(6px)" bg="blackAlpha.600" />
            <ModalContent dir="rtl" borderRadius="2xl" border="3px solid" borderColor={PRIMARY} mx={4}>
                <ModalHeader bg={DARK} borderTopRadius="xl" pb={4}>
                    <VStack spacing={1} align="start">
                        <HStack>
                            <Icon as={MdCalendarMonth} color={PRIMARY} boxSize={6} />
                            <Text color="white" fontWeight="800" fontSize="lg">اختر جدول اوقات يناسبك</Text>
                        </HStack>
                        <Badge colorScheme="yellow" px={3} py={1} borderRadius="full" fontSize="sm">
                            {pendingForm.matiere}
                        </Badge>
                    </VStack>
                </ModalHeader>
                <ModalCloseButton color="white" top={3} />

                <ModalBody py={5}>
                    {submitted ? (
                        <VStack py={4} spacing={4} textAlign="center">
                            <Icon as={MdCheckCircle} boxSize={16} color="green.400" />
                            <Heading size="md" color={DARK}>تم إرسال إجابتك ✅</Heading>
                            <Text color="gray.500">تم تسجيل اختيارك. سيتم التواصل معك عبر الواتساب.</Text>
                            <Box p={4} bg="yellow.50" borderRadius="xl" border="1px solid" borderColor="yellow.200" w="100%">
                                <HStack justify="center">
                                    <Badge colorScheme="blue" px={3} py={1} borderRadius="full">الحصة الأولى: {session1Choice}</Badge>
                                    <Badge colorScheme="purple" px={3} py={1} borderRadius="full">الحصة الثانية: {session2Choice}</Badge>
                                </HStack>
                            </Box>
                            <Button onClick={handleDone} bg={PRIMARY} color={DARK} fontWeight="700" borderRadius="xl" w="100%" size="lg">
                                حسناً 👍
                            </Button>
                        </VStack>
                    ) : (
                        <VStack spacing={4} align="stretch">
                            <FormControl isRequired>
                                <FormLabel fontWeight="700" color={DARK} fontSize="sm">اسمك و لقبك</FormLabel>
                                <Input value={fullName} onChange={e => setFullName(e.target.value)}
                                    placeholder="مثال: محمد بن علي" borderRadius="xl" focusBorderColor={PRIMARY} />
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel fontWeight="700" color={DARK} fontSize="sm">رقم الواتساب الي داخل بيه فالقروب</FormLabel>
                                <Input value={whatsappNumber} onChange={e => setWhatsappNumber(e.target.value)}
                                    placeholder="21612345678" borderRadius="xl" dir="ltr" type="tel" focusBorderColor={PRIMARY} />
                            </FormControl>

                            <Divider />

                            <FormControl isRequired>
                                <FormLabel fontWeight="700" color={DARK} fontSize="sm">الحصة الأولى</FormLabel>
                                <RadioGroup value={session1Choice} onChange={setSession1Choice}>
                                    <VStack align="stretch" spacing={2}>
                                        {pendingForm.session1Options.map((opt, i) => (
                                            <Box key={i} p={2} borderRadius="lg" border="2px solid"
                                                borderColor={session1Choice === opt ? PRIMARY : 'gray.200'}
                                                bg={session1Choice === opt ? 'yellow.50' : 'white'}
                                                cursor="pointer" onClick={() => setSession1Choice(opt)}
                                                transition="all 0.15s" _hover={{ borderColor: PRIMARY }}>
                                                <Radio value={opt} colorScheme="yellow">
                                                    <Text fontSize="sm" fontWeight={session1Choice === opt ? '700' : '400'}>{opt}</Text>
                                                </Radio>
                                            </Box>
                                        ))}
                                    </VStack>
                                </RadioGroup>
                            </FormControl>

                            <FormControl isRequired>
                                <FormLabel fontWeight="700" color={DARK} fontSize="sm">الحصة الثانية</FormLabel>
                                <RadioGroup value={session2Choice} onChange={setSession2Choice}>
                                    <VStack align="stretch" spacing={2}>
                                        {pendingForm.session2Options.map((opt, i) => (
                                            <Box key={i} p={2} borderRadius="lg" border="2px solid"
                                                borderColor={session2Choice === opt ? PRIMARY : 'gray.200'}
                                                bg={session2Choice === opt ? 'yellow.50' : 'white'}
                                                cursor="pointer" onClick={() => setSession2Choice(opt)}
                                                transition="all 0.15s" _hover={{ borderColor: PRIMARY }}>
                                                <Radio value={opt} colorScheme="yellow">
                                                    <Text fontSize="sm" fontWeight={session2Choice === opt ? '700' : '400'}>{opt}</Text>
                                                </Radio>
                                            </Box>
                                        ))}
                                    </VStack>
                                </RadioGroup>
                            </FormControl>
                        </VStack>
                    )}
                </ModalBody>

                {!submitted && (
                    <ModalFooter pt={0} pb={4} px={6}>
                        <Button onClick={handleSubmit} isLoading={submitting} loadingText="جاري الإرسال..."
                            bg={PRIMARY} color={DARK} fontWeight="800" borderRadius="xl" w="100%" size="lg"
                            leftIcon={<MdSend />} _hover={{ bg: '#d4ac00' }}>
                            إرسال الاختيار
                        </Button>
                    </ModalFooter>
                )}
            </ModalContent>
        </Modal>
    );
}
